import { getQuestRepository, hasQuestRepository } from './questRepository'
import type { QuestRecordOverrides } from './schemas'

// A quest references another submission through `recordOverrides`, keyed by the
// target submissionId and living inside the AUTHOR quest rather than the target,
// so nothing cascades when the target is deleted — hence this sweep.
//
// Dropped rather than emptied, so an absent key stays the only representation of
// "no reference". A lingering empty entry would make `anyQuestAffectsSubmissions`
// report that the quest changes the base submissions when it no longer does,
// costing a needless scenario rebuild.
const withoutTarget = <T extends QuestRecordOverrides>(
  map: T | undefined,
  targetId: string
): T | undefined => {
  if (map?.[targetId] === undefined) return map
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [targetId]: _dropped, ...rest } = map
  return (Object.keys(rest).length === 0 ? undefined : rest) as T | undefined
}

// After a submission (quest or base survey) is deleted, iterate all quests and
// strip every reference to it. Called from the delete mutations.
//
// No-op when no quest repository has been registered (e.g. the portal has no
// quest concept). Otherwise this would throw and block submission deletes.
const stripQuestReferencesToSubmission = async (
  deletedSubmissionId: string
): Promise<void> => {
  if (!hasQuestRepository()) return
  const repo = getQuestRepository()
  const quests = await repo.list()
  for (const quest of quests) {
    const recordOverrides = withoutTarget(
      quest.entity.recordOverrides,
      deletedSubmissionId
    )
    // Identity comparison: `withoutTarget` returns its input untouched when
    // there was nothing to remove, so this skips quests that never referenced it.
    if (recordOverrides === quest.entity.recordOverrides) continue
    await repo.update(quest.id, { ...quest.entity, recordOverrides })
  }
}

export default stripQuestReferencesToSubmission
